<template>
  <!-- MIN CONCENTRATION FACTOR -->
  <div class="form-group row">
    <form-label for-i-d="edit_lineitem_minimum_audience_concentration">
      <span>{{ capitalize(i18n.t('messages.minimumAudienceConcentration')) }}</span>
    </form-label>
    <div :class="`col-sm-${columnWidth}`">
      <tabs-slider
        id="min_audience_concentration_tabs"
        width="100%"
        height="5rem"
        tab-style="max-width: 25%; padding: 1.2rem 2.9rem; text-align: center"
        :is-disabled="isDisabled"
        :selected-tab-i-d="isDisabled ? 'disabled' : selectedTabID"
        :tabs="[
          { id: 'disabled', name: capitalize(i18n.t('states.disabled')) },
          { id: 'market-average', name: capitalize(i18n.t('messages.marketAverage')) },
          { id: 'concentration-index', name: capitalize(i18n.t('messages.concentrationIndex')) },
          { id: 'concentration-percentage', name: capitalize(i18n.t('messages.concentrationPercentage')) },
        ]"
        @selected-tab-changed="(tab) => onSelectedTabChanged(tab.id)" />
      <span
        v-show="$validator.messageBag.errors.has('minConcentrationFactor')"
        class="help is-danger"
        >{{ $validator.messageBag.errors.first('minConcentrationFactor') }}</span
      >
    </div>
    <div
      v-if="columnWidth === 5"
      class="col-sm-3" />
    <div class="col-sm-1 field-help">
      <div v-tooltip="capitalize(i18n.t('messages.minAudienceConcentrationDescription'))">
        <icon-info />
      </div>
    </div>
  </div>
  <div
    v-if="isCustomTabID"
    class="form-group row">
    <div
      class="offset-sm-3"
      :class="`col-sm-${columnWidth - 1}`">
      <label
        v-if="isConcentrationPercentage"
        class="control-label"
        style="width: 100%">
        {{ capitalize(i18n.t('messages.audienceConcentrationThreshold')) }}
      </label>
      <label
        v-else
        class="control-label"
        style="width: 100%">
        {{ capitalize(i18n.t('messages.audienceConcentrationRatio')) }}
      </label>
    </div>
    <div class="col-sm-1">
      <formatted-input
        name="customMinimumAudienceConcentration"
        :is-percentage="isConcentrationPercentage"
        :min="minConcentration"
        :max="maxConcentration"
        :value="minConcentrationFactor"
        :format="formatConcentration"
        :validation-errors="$validator.messageBag.errors"
        @value-changed="
          (x) => {
            minConcentrationFactor = x;
          }
        " />
    </div>
    <div
      v-if="!isConcentrationPercentage"
      class="col-sm-1 field-help">
      <div v-tooltip="capitalize(i18n.t('messages.audienceConcentrationRatioDescription'))">
        <icon-info />
      </div>
    </div>
  </div>
  <div
    v-if="showApply && isConcentrationPercentage"
    class="form-group row justify-content-end">
    <div>
      <label
        class="control-label"
        style="width: 100%">
        {{ capitalize(i18n.t('actions.applyConcentrationFilter')) }}
      </label>
    </div>
    <div>
      <button
        class="btn btn-danger btn-center ml-5"
        :disabled="minConcentrationFactor === 0 && minConcentrationValue <= 0.00001"
        style="width: fit-content"
        @click="isRemoveUnitsModalVisible = true">
        {{ capitalize(i18n.t('actions.applyConcentrationFilterLabel')) }}
      </button>
    </div>
    <div class="col-sm-1 field-help ml-4">
      <div
        v-tooltip="capitalize(i18n.t('messages.applyConcentrationFilterDescription'))"
        class="mr-0">
        <icon-info />
      </div>
    </div>
  </div>
  <modal
    :visible="isRemoveUnitsModalVisible"
    :title="capitalize(i18n.t('actions.applyConcentrationFilter'))"
    :body="sentenceCase(i18n.t('messages.applyConcentrationFilterConfirm'))"
    :button1-caption="capitalize(i18n.t('actions.confirm'))"
    :button2-caption="capitalize(i18n.t('actions.cancel'))"
    :button2-visible="true"
    @confirmed="applyConcentrationFilterToUnits"
    @cancelled="() => (isRemoveUnitsModalVisible = false)" />
</template>
<script setup lang="ts">
  import { capitalize, computed, inject, onMounted, ref, watch } from 'vue';
  import { useI18n } from 'vue-i18n';

  import { FormattedInput, FormLabel, TabsSlider } from '@/common/components/controls';
  import Modal from '@/common/components/controls/Modal.vue';
  import { IconInfo } from '@/common/icons/monochrome';
  import { LineItem, Plan } from '@/common/models';
  import { sentenceCase } from '@/globals';
  import Validator from '@/validator/Validator';

  // Setup
  interface Props {
    entity: LineItem | Plan;
    columnWidth: number;
    isDisabled: boolean;
    showApply: boolean;
  }
  interface Emits {
    (e: 'changed', value: { minConcentrationFactor: number; minConcentrationValue: number }): void;
    (e: 'apply', minConcentrationValue: number): void;
  }
  const props = defineProps<Props>();
  const emits = defineEmits<Emits>();
  const i18n = useI18n();
  const $validator: Validator = inject('validator');

  // Component Data
  type SelectedTab = 'disabled' | 'concentration-index' | 'concentration-percentage' | 'market-average';
  const selectedTabID = ref<SelectedTab>('disabled');
  const isRemoveUnitsModalVisible = ref(false);
  const isCustomTabID = computed(
    () => selectedTabID.value === 'concentration-index' || selectedTabID.value === 'concentration-percentage',
  );

  // Concentration data
  const minConcentrationFactor = ref(0);
  const minConcentrationValue = ref(0);
  const isConcentrationPercentage = computed(() => selectedTabID.value === 'concentration-percentage');
  const minConcentration = computed(() => (isConcentrationPercentage.value ? 0 : 0.02));
  const maxConcentration = computed(() => (isConcentrationPercentage.value ? 100 : 10));
  const formatConcentration = computed(() => (isConcentrationPercentage.value ? '0[.]0' : '0,0.00'));

  // Functions
  function onSelectedTabChanged(tabID: SelectedTab) {
    selectedTabID.value = tabID;
    switch (tabID) {
      case 'disabled':
        minConcentrationValue.value = 0;
        minConcentrationFactor.value = 0;
        break;
      case 'market-average':
      case 'concentration-index':
        minConcentrationFactor.value = 1;
        break;
      default:
        minConcentrationFactor.value = 0;
        break;
    }
  }
  async function applyConcentrationFilterToUnits() {
    emits('apply', minConcentrationValue.value);
    isRemoveUnitsModalVisible.value = false;
    onSelectedTabChanged('disabled');
  }

  // Setup
  onMounted(() => {
    // Set tab and values
    if (props.entity.minConcentrationFactor || props.entity.minConcentrationValue) {
      if (props.entity.minConcentrationFactor === 0 && props.entity.minConcentrationValue === 0) {
        onSelectedTabChanged('disabled');
      } else if (props.entity.minConcentrationFactor === 1 && props.entity.minConcentrationValue === 0) {
        onSelectedTabChanged('market-average');
      } else if (props.entity.minConcentrationValue === 0) {
        onSelectedTabChanged('concentration-index');
      } else {
        onSelectedTabChanged('concentration-percentage');
      }
      minConcentrationFactor.value = props.entity.minConcentrationFactor || props.entity.minConcentrationValue * 100;
    }
  });
  // Update min concentration value when factor changes
  watch(minConcentrationFactor, (value) => {
    if (value != null) {
      if (isConcentrationPercentage.value) {
        minConcentrationValue.value = +(value / 100).toFixed(3); // BE accepts 3 decimal
        emits('changed', {
          minConcentrationFactor: 0,
          minConcentrationValue: minConcentrationValue.value,
        });
      } else {
        minConcentrationValue.value = 0;
        emits('changed', {
          minConcentrationFactor: Math.round(100 * value) / 100,
          minConcentrationValue: 0,
        });
      }
    }
  });
</script>
